import { useState } from 'react';
import { KeyRound, Phone, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { phoneToUsername } from '../../auth/cognitoClient';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import styles from '../../styles/authShell.module.css';

export default function ConfirmAccount({ initialPhone = '', initialPin = '', onDone }) {
  const { confirmSignUp, resendCode } = useAuth();
  const [phone, setPhone] = useState(initialPhone);
  const [pin, setPin] = useState(initialPin);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const username = phoneToUsername(phone);
      const session = await confirmSignUp(username, code, { phone, pin });
      onDone(session);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResent(false);
    setResending(true);
    try {
      await resendCode(phoneToUsername(phone));
      setResent(true);
    } catch (err) {
      setError(err.message || 'Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <p className={styles.eyebrow}>Confirm your account</p>
      <h2 className={styles.formTitle}>Enter the code we emailed you</h2>
      <p className={styles.formSubtitle}>Check your inbox for a 6-digit confirmation code.</p>

      <AuthMessage>{error}</AuthMessage>
      {resent && <AuthMessage tone="success">A new code was sent.</AuthMessage>}

      <label className={styles.field}>
        <span>Phone number</span>
        <div className={styles.inputWrap}>
          <Phone size={15} />
          <input
            type="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91XXXXXXXXXX"
          />
        </div>
      </label>

      <label className={styles.field}>
        <span>4-digit PIN</span>
        <div className={styles.inputWrap}>
          <KeyRound size={15} />
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            required
            className={styles.pinInput}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="••••"
          />
        </div>
      </label>

      <label className={styles.field}>
        <span>Confirmation code</span>
        <div className={styles.inputWrap}>
          <ShieldCheck size={15} />
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
          />
        </div>
      </label>

      <Button
        type="submit"
        size="lg"
        disabled={loading || pin.length !== 4 || code.length !== 6 || !phone}
        className={styles.submit}
      >
        {loading ? 'Confirming…' : 'Confirm account'}
      </Button>

      <p className={styles.helper}>
        Didn't get a code?{' '}
        <button type="button" className={styles.linkBtn} onClick={handleResend} disabled={resending || !phone}>
          {resending ? 'Sending…' : 'Resend code'}
        </button>
      </p>
    </form>
  );
}
